<template>
  <div class="container mx-auto px-4 py-2">
    <!-- Заголовок -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        <button @click="goBack" class="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300">Назад</button>
        <h1 class="text-xl font-semibold">{{ list.name || 'Список материалов' }}</h1>
      </div>
      <button
        @click="exportExcel"
        :disabled="!list.items || list.items.length === 0"
        class="px-4 py-2 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Выгрузить в Excel
      </button>
    </div>

    <!-- Сканер: компактный индикатор фокуса -->
    <div
      v-can-write="dataCollectionModuleId"
      @click="focusInput"
      :class="[
        'flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border cursor-pointer text-sm select-none',
        isInputFocused ? 'border-green-400 bg-green-50 text-green-700' : 'border-gray-300 bg-gray-50 text-gray-600'
      ]"
    >
      <!-- Скрытый input сканера -->
      <QRTextInput
        ref="qrTextInput"
        v-model="qrValue"
        class="hidden-input"
        placeholder="Отсканируйте QR-код продукции"
        @scan-complete="handleScan"
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
      />
      <span v-if="scanningStatus">{{ scanningStatus }}</span>
      <template v-else>
        <span class="inline-block w-2 h-2 rounded-full" :class="isInputFocused ? 'bg-green-500' : 'bg-gray-400'"></span>
        <span>{{ isInputFocused ? 'Сканер активен — отсканируйте продукцию' : 'Нажмите, чтобы активировать сканер' }}</span>
      </template>
    </div>

    <!-- Ручной ввод: поиск номенклатуры на складе -->
    <div v-can-write="dataCollectionModuleId" class="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <label class="block text-xs text-gray-600 mb-1">Ручной ввод (поиск номенклатуры на складе)</label>
      <v-select
        v-model="manualSelected"
        :options="manualOptions"
        :filterable="false"
        :loading="manualLoading"
        label="product_name"
        placeholder="Введите артикул или название..."
        @search="onManualSearch"
        @option:selected="onManualSelected"
      >
        <template #option="option">
          <span>{{ optionLabel(option) }}</span>
        </template>
        <template #selected-option="option">
          <span>{{ optionLabel(option) }}</span>
        </template>
        <template #no-options="{ search }">
          <span v-if="!search">Введите символ для поиска…</span>
          <span v-else-if="manualLoading">Поиск…</span>
          <span v-else>Не найдено</span>
        </template>
      </v-select>
    </div>

    <!-- Позиции списка -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-3 py-2 text-left border-b">Артикул</th>
            <th class="px-3 py-2 text-left border-b">Название</th>
            <th class="px-3 py-2 text-left border-b">Серийный номер</th>
            <th class="px-3 py-2 text-left border-b w-28">Количество</th>
            <th class="px-3 py-2 text-left border-b">Место хранения</th>
            <th class="px-3 py-2 text-right border-b w-24">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">Загрузка...</td>
          </tr>
          <tr v-else-if="list.items && list.items.length === 0">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">Позиций нет — отсканируйте или добавьте вручную</td>
          </tr>
          <tr v-for="item in list.items" :key="item.id" class="border-t border-gray-200">
            <td class="px-3 py-2">{{ item.sku || '—' }}</td>
            <td class="px-3 py-2">{{ item.product_name }}</td>
            <td class="px-3 py-2">{{ item.serial_number || '—' }}</td>
            <td class="px-3 py-2">{{ formatQuantity(item.count) }} {{ item.unit || 'шт' }}</td>
            <td class="px-3 py-2">
              {{ item.storage_location }}<template v-if="item.warehouse_name"> ({{ item.warehouse_name }})</template>
            </td>
            <td class="px-3 py-2 text-right">
              <button v-can-write="dataCollectionModuleId" @click="removeItem(item)" class="px-2 py-1 text-red-600 hover:underline">Удалить</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Модалка подтверждения / выбора размещения -->
    <div v-if="choiceVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-lg p-6">
        <h2 class="text-lg font-bold mb-4">{{ choicePlacements.length > 1 ? 'Выберите размещение' : 'Подтвердите добавление' }}{{ choiceProductName ? ': ' + choiceProductName : '' }}</h2>
        <div class="flex flex-col gap-2 max-h-80 overflow-y-auto">
          <button
            v-for="(p, idx) in choicePlacements"
            :key="idx"
            @click="choiceSelectedSpid = p.storehouse_product_id"
            :class="[
              'text-left px-3 py-2 border rounded',
              choiceSelectedSpid === p.storehouse_product_id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
            ]"
          >
            <div class="font-semibold">
              <template v-if="p.is_single">{{ p.serial_number || '—' }}</template>
              <template v-else-if="p.is_package">Упаковка {{ p.storehouse_product_id }} — {{ formatQuantity(p.count) }} {{ p.unit || 'шт' }}</template>
              <template v-else>{{ formatQuantity(p.count) }} {{ p.unit || 'шт' }}</template>
            </div>
            <div class="text-xs text-gray-500">
              {{ p.full_address }}<template v-if="p.warehouse_name"> ({{ p.warehouse_name }})</template>
            </div>
          </button>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button @click="closeChoice" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300">
            Отмена
          </button>
          <button
            @click="confirmChoice"
            :disabled="!choiceSelectedSpid"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Подтвердить
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка уведомления об ошибке -->
    <div v-if="errorVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <p class="text-sm text-gray-800 mb-4">{{ errorMessage }}</p>
        <div class="flex justify-end">
          <button @click="errorVisible = false" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700">
            Понятно
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import QRTextInput from '../../../../modules/storehouse/resources/js/components/QRTextInput.vue';
import { useAuthStore } from '@/stores/auth';

export default {
  name: 'DataCollectionShow',
  components: { QRTextInput },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      list: { id: null, name: '', items: [] },
      loading: false,
      qrValue: '',
      scanningStatus: '',
      isInputFocused: false,
      manualSelected: null,
      manualOptions: [],
      manualLoading: false,
      manualSearchTimer: null,
      choiceVisible: false,
      choicePlacements: [],
      choiceProductName: '',
      choiceSelectedSpid: null,
      errorVisible: false,
      errorMessage: '',
    };
  },
  computed: {
    // В маршруте лежит id списка, не id модуля: берём модуль «Сбор данных»
    // из модулей пользователя по ключу data-collection
    dataCollectionModuleId() {
      const modules = useAuthStore().user?.modules || [];
      return modules.find(m => m.key === 'data-collection')?.id ?? null;
    },
  },
  mounted() {
    this.loadOne();
    this.focusInput();
  },
  methods: {
    async loadOne() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/data-collection/${this.id}`);
        if (data?.success) {
          this.list = data.data;
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось загрузить список');
      } finally {
        this.loading = false;
      }
    },
    focusInput() {
      this.$nextTick(() => {
        if (this.$refs.qrTextInput) {
          this.$refs.qrTextInput.forceFocus();
          this.isInputFocused = true;
        }
      });
    },
    parseQR(raw) {
      const params = new URLSearchParams(raw);
      const result = {};
      for (const [key, value] of params) {
        result[key] = value;
      }
      return result;
    },
    async handleScan(raw) {
      if (!raw || raw.trim() === '' || this.scanningStatus) return;
      this.scanningStatus = 'Обработка QR-кода...';
      const parsed = this.parseQR(raw);
      const params = {};
      if (parsed.spid) {
        params.spid = parsed.spid;
      } else if (parsed.bpid || parsed.pid) {
        params.bpid = parsed.bpid || parsed.pid;
      } else {
        this.scanningStatus = '';
        this.qrValue = '';
        this.notifyError(null, 'Неопознанный формат QR-кода');
        this.focusInput();
        return;
      }
      try {
        // Резолв через тот же эндпоинт, что и ТСД
        const { data } = await axios.get('/api/tsdScan/scanProductByBpid', { params });
        if (!data?.success || !data?.data) {
          this.notifyError(null, data?.message || 'Не удалось получить данные о продукции');
        } else {
          const product = data.data;
          const placements = (product.placements || []).map(p => ({
            ...p,
            is_single: product.is_single,
            serial_number: product.serial_number,
            unit: product.unit_short_name,
          }));
          if (placements.length === 0) {
            this.notifyError(null, 'Продукция не размещена на складе');
          } else {
            // Всегда открываем модалку (подтверждение); если место одно — сразу выбрано
            this.choicePlacements = placements;
            this.choiceProductName = product.product_name || '';
            this.choiceSelectedSpid = placements.length === 1 ? placements[0].storehouse_product_id : null;
            this.choiceVisible = true;
          }
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось обработать скан');
      } finally {
        this.scanningStatus = '';
        this.qrValue = '';
        this.focusInput();
      }
    },
    async addItem(payload) {
      try {
        const { data } = await axios.post('/api/data-collection/add-item', payload);
        if (!data?.success) {
          this.notifyError(null, data?.message || 'Не удалось добавить позицию');
          return;
        }
        const result = data.data;
        if (result.added) {
          await this.loadOne();
        } else if (result.needs_choice) {
          // Количественная с несколькими размещениями — выбор места
          this.choicePlacements = result.placements || [];
          this.choiceProductName = result.product_name || '';
          this.choiceVisible = true;
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось добавить позицию');
      }
    },
    async addBySpid(spid) {
      this.choiceVisible = false;
      await this.addItem({ list_id: this.id, spid: Number(spid) });
      this.focusInput();
    },
    confirmChoice() {
      if (!this.choiceSelectedSpid) return;
      const spid = this.choiceSelectedSpid;
      this.choiceSelectedSpid = null;
      this.addBySpid(spid);
    },
    closeChoice() {
      this.choiceVisible = false;
      this.choiceSelectedSpid = null;
      this.focusInput();
    },
    onManualSearch(search) {
      clearTimeout(this.manualSearchTimer);
      this.manualSearchTimer = setTimeout(() => this.loadManual(search || ''), 400);
    },
    async loadManual(search) {
      if (!search || search.trim() === '') {
        this.manualOptions = [];
        return;
      }
      this.manualLoading = true;
      try {
        const { data } = await axios.get('/api/data-collection/search-products', { params: { search } });
        this.manualOptions = data?.data || [];
      } catch (e) {
        this.manualOptions = [];
        console.error(e);
      } finally {
        this.manualLoading = false;
      }
    },
    async onManualSelected(option) {
      if (!option) return;
      const placements = option.placements || [];
      if (placements.length === 0) {
        this.notifyError(null, 'Нет активных размещений продукции на складе');
      } else {
        // Та же модалка подтверждения; если место одно — сразу выбрано
        this.choicePlacements = placements;
        this.choiceProductName = option.product_name;
        this.choiceSelectedSpid = placements.length === 1 ? placements[0].storehouse_product_id : null;
        this.choiceVisible = true;
      }
      this.manualSelected = null;
      this.manualOptions = [];
    },
    optionLabel(option) {
      // Артикул + название (если артикул есть), иначе только название; + остаток с единицей
      const name = option.sku ? `${option.sku} ${option.product_name}` : option.product_name;
      const unit = option.unit || 'шт';
      return `${name} — остаток ${this.formatQuantity(option.total_count)} ${unit}`;
    },
    async removeItem(item) {
      if (!confirm('Удалить позицию из списка?')) return;
      try {
        await axios.delete(`/api/data-collection/items/${item.id}`);
        await this.loadOne();
      } catch (e) {
        this.notifyError(e, 'Не удалось удалить позицию');
      }
    },
    async exportExcel() {
      try {
        const response = await axios.get(`/api/data-collection/${this.id}/export`, { responseType: 'blob' });
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.list.name || 'data_collection'}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.notifyError(e, 'Не удалось выгрузить файл');
      }
    },
    goBack() {
      this.$router.push({ name: 'DataCollection' });
    },
    formatQuantity(val) {
      if (val == null) return '0';
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },
    notifyError(error, fallback) {
      this.errorMessage = error?.response?.data?.message || fallback || 'Произошла ошибка, попробуйте ещё раз';
      if (error) {
        console.error(error);
      }
      this.errorVisible = true;
    },
  },
};
</script>

<style scoped>
.hidden-input {
  position: absolute !important;
  top: -9999px !important;
  left: -9999px !important;
  width: 1px !important;
  height: 1px !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>
